import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { CashField, CashNotice } from './CashPrimitives';
import { CashPeriodPicker, cashPeriodValid } from './CashPeriodPicker';
import { cashAmountInput, parseCashAmount } from './cashMoney';
/** A correction carries an explicit replacement allocation; typing its total never redistributes it. */
export function CashCorrectionPeriods({ assessment, locale, onChange, onDirty, }) {
    const initial = assessment.target.periods, [amounts, setAmounts] = useState(() => Object.fromEntries(initial.allocations.map(a => [
        a.basisId,
        cashAmountInput(a.amountMinor, assessment.currency),
    ])));
    const [month, setMonth] = useState(initial.advance?.month ?? ''), [advance, setAdvance] = useState(initial.advance ? cashAmountInput(initial.advance.amountMinor, assessment.currency) : '0');
    const copy = {
        ru: {
            title: 'Исправленное распределение',
            amount: 'Сумма периода',
            advance: 'Целевой аванс',
            month: 'Месяц аванса',
            meaning: 'Задайте каждую часть явно. Сумма распределения равна исправленной выплате за вычетом уже выполненных возвратов. Изменение общей суммы не меняет назначения автоматически.',
        },
        az: {
            title: 'Düzəldilmiş bölgü',
            amount: 'Dövrün məbləği',
            advance: 'Məqsədli avans',
            month: 'Avans ayı',
            meaning: 'Hər hissəni ayrıca təyin edin. Bölgü düzəldilmiş ödənişdən əvvəlki qaytarmalar çıxılmaqla bərabər olmalıdır. Ümumi məbləğ təyinatları avtomatik dəyişmir.',
        },
        en: {
            title: 'Replacement allocation',
            amount: 'Period amount',
            advance: 'Targeted advance',
            month: 'Advance month',
            meaning: 'Specify each part explicitly. Allocations must equal the replacement payment less existing refunds. Changing the total never changes the assignments automatically.',
        },
    }[locale];
    const allocations = Object.entries(amounts).map(([basisId, amount]) => ({
        basisId,
        amountMinor: parseCashAmount(amount, true, assessment.currency),
    })), advanceMinor = parseCashAmount(advance, true, assessment.currency);
    const valid = allocations.every(a => a.amountMinor !== null) &&
        advanceMinor !== null &&
        (advanceMinor === '0' || cashPeriodValid(month));
    const target = valid
        ? {
            mode: 'BASIS',
            periods: {
                ...initial,
                allocations: allocations
                    .filter(a => a.amountMinor !== '0')
                    .map(a => ({ basisId: a.basisId, amountMinor: a.amountMinor })),
                advance: advanceMinor !== '0' ? { month, amountMinor: advanceMinor } : undefined,
            },
        }
        : undefined;
    const serialized = JSON.stringify(target ?? null);
    useEffect(() => onChange(JSON.parse(serialized) ?? undefined), [serialized, onChange]);
    return (_jsxs("section", { className: "cash-fields", children: [_jsx("h3", { children: copy.title }), _jsx(CashNotice, { children: copy.meaning }), initial.allocations.map(a => (_jsx(CashField, { label: `${copy.amount}: ${assessment.allocations.find(p => p.basisId === a.basisId)?.title ?? a.basisId}`, children: _jsx("input", { inputMode: "decimal", value: amounts[a.basisId], onChange: e => setAmounts(before => ({ ...before, [a.basisId]: e.target.value })) }) }, a.basisId))), initial.advance && (_jsxs(_Fragment, { children: [_jsx(CashField, { label: copy.advance, children: _jsx("input", { inputMode: "decimal", value: advance, onChange: e => setAdvance(e.target.value) }) }), _jsx(CashPeriodPicker, { label: copy.month, locale: locale, value: month, onChange: value => {
                            onDirty?.(true);
                            setMonth(value);
                        } })] }))] }));
}
